import { Box, Divider } from '@mui/material'
import { useContext } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { BoxForm, NumberField, RadioGroup, Select, Slider, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import ProductAdUnitsTable from '../../components/ProductAdUnitsTable'
import { AddEditDomainContext } from '../../contexts/AddEditDomainContext'
import { useLazyLoadRadioGroup } from './hooks/useLazyLoadRadioGroup'
import { useScrollSlowingIntensityOptions } from './hooks/useScrollSlowingIntensityOptions'
import { useTooltips } from './hooks/useTooltips'
import { useTrafficAffectedMarks } from './hooks/useTrafficAffectedMarks'
import { Schema } from './schema'
import { AdHolding } from './sections/AdHolding'

export const Form = (): React.JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation('features/domain', { keyPrefix: 'form.viewabilityTools' })

  const { domain } = useContext(AddEditDomainContext)
  if (!domain) {
    throw Error('Domain should be accessible')
  }

  const { control, watch } = useFormContext<Schema>()

  const {
    lazyLoadingEnabled,
    lazyLoadTargetingPrebidAuctionEnabled,
    lazyLoadType,
    scrollSlowingEnabled,
  } = watch()

  const lazyLoadOptions = useLazyLoadRadioGroup()
  const tooltips = useTooltips()
  const trafficAffectedMarks = useTrafficAffectedMarks()

  const cannotUpdateDomain: boolean = ability.cannot('update', 'Domain')
  const cannotUpdateViewabilityTools: boolean = ability.cannot('update', 'Product-viewabilityTools')

  const scrollSlowingIntensityOptions = useScrollSlowingIntensityOptions()

  return (
    <>
      <Paper>
        <SectionHeader
          subtitle={t('enabled.description')}
          title={t('enabled.title')}
          titleEndAdornment={
            <Switch
              control={control}
              disabled={cannotUpdateDomain || cannotUpdateViewabilityTools}
              name='viewabilityToolsEnabled'
            />
          }
        />

        <GapBox />

        <BoxForm>
          <Slider
            control={control}
            disabled={cannotUpdateDomain}
            label={t('trafficAffected.label')}
            marks={trafficAffectedMarks}
            name='trafficAffected'
            tooltip={{ content: t('trafficAffected.tooltip') }}
            withInput
          />
        </BoxForm>
      </Paper>

      <Paper>
        <SectionHeader
          subtitle={t('basicSettings.subtitle')}
          title={t('basicSettings.title')}
        />

        <GapBox />

        <BoxForm>
          <AdHolding />

          <Divider />

          <Switch
            control={control}
            disabled={cannotUpdateDomain}
            label={t('basicSettings.scrollSlowingEnabled.label')}
            labelPlacement='start'
            name='scrollSlowingEnabled'
            tooltip={tooltips.scrollSlownig}
          />

          {scrollSlowingEnabled && (
            <Box sx={{ ml: CONTAINER_SPACING }}>
              <Select
                control={control}
                disabled={cannotUpdateDomain}
                label={t('basicSettings.scrollSlowingEnabled.scrollSlowingIntensity.label')}
                name='scrollSlowingIntensity'
                options={scrollSlowingIntensityOptions}
                tooltip={{
                  content: t('basicSettings.scrollSlowingEnabled.scrollSlowingIntensity.tooltip'),
                }}
              />
            </Box>
          )}

          <Divider />

          <Switch
            control={control}
            disabled={cannotUpdateDomain}
            label={t('basicSettings.lazyLoadingEnabled.label')}
            labelPlacement='start'
            name={'lazyLoadingEnabled'}
            tooltip={tooltips.lazyLoading}
          />

          {lazyLoadingEnabled && (
            <BoxForm sx={{ ml: CONTAINER_SPACING }}>
              <RadioGroup
                control={control}
                disabled={cannotUpdateDomain}
                name='lazyLoadType'
                options={lazyLoadOptions}
                row
              />

              <Switch
                control={control}
                disabled={cannotUpdateDomain}
                label={t(
                  'basicSettings.lazyLoadingEnabled.options.lazyLoadTargetingPrebidAuctionEnabled.label',
                )}
                labelPlacement='start'
                name={'lazyLoadTargetingPrebidAuctionEnabled'}
                tooltip={tooltips.lazyLoadTargetingPrebidAuctionEnabled}
              />

              {lazyLoadTargetingPrebidAuctionEnabled && (
                <NumberField
                  control={control}
                  disabled={cannotUpdateDomain}
                  label={t(
                    'basicSettings.lazyLoadingEnabled.options.kvTargetingAndPrebidAuction.label',
                  )}
                  name='kvTargetingAndPrebidAuction'
                  sx={{ ml: CONTAINER_SPACING }}
                  tooltip={tooltips.kvTargetingAndPrebidAuction}
                  width='md'
                />
              )}

              <NumberField
                control={control}
                disabled={cannotUpdateDomain}
                label={t('basicSettings.lazyLoadingEnabled.options.gamAdRequestMargin.label')}
                name='gamAdRequestMargin'
                tooltip={tooltips.gamAdRequestMargin}
                width='md'
              />

              {lazyLoadType === 'googlePublisherTagsLazyLoad' && (
                <NumberField
                  control={control}
                  disabled={cannotUpdateDomain}
                  label={t('basicSettings.lazyLoadingEnabled.options.gamAdRenderMargin.label')}
                  name='gamAdRenderMargin'
                  tooltip={tooltips.gamAdRenderMargin}
                  width='md'
                />
              )}

              <NumberField
                control={control}
                disabled={cannotUpdateDomain}
                label={t('basicSettings.lazyLoadingEnabled.options.mobileScaling.placeholder')}
                name='mobileScaling'
                tooltip={tooltips.mobileScaling}
                width='md'
              />
            </BoxForm>
          )}
        </BoxForm>
      </Paper>

      <Controller
        control={control}
        name='adUnitsAttributes'
        render={() => (
          <ProductAdUnitsTable
            domainId={domain.id}
            product='viewabilityTools'
          />
        )}
      />
    </>
  )
}
